import { useEffect, useState, useCallback } from 'react';
import { Download, RefreshCw, Mail, Eye, Search, Filter, X, Undo2, Inbox } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { adminOrdersAPI } from '../../services/api';

const money = (n, ccy) => {
  const c = (ccy || 'AUD').toUpperCase();
  try { return `$${Number(n || 0).toFixed(2)} ${c}`; } catch { return `${n} ${c}`; }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const fulfilmentColor = (s) => {
  switch (s) {
    case 'shipped': return 'bg-emerald-100 text-emerald-700';
    case 'cancelled': return 'bg-red-100 text-red-700';
    case 'pending':
    default: return 'bg-amber-100 text-amber-700';
  }
};

const OrderDetail = ({ order, onClose, onChanged }) => {
  const [savingStatus, setSavingStatus] = useState('');
  const [resending, setResending] = useState(false);
  const [tracking, setTracking] = useState(order?.tracking_number || '');
  const [carrier, setCarrier] = useState(order?.carrier || '');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [refunding, setRefunding] = useState(false);

  // Keep local tracking inputs in sync when a different order is opened
  useEffect(() => {
    setTracking(order?.tracking_number || '');
    setCarrier(order?.carrier || '');
    setRefundAmount('');
    setRefundReason('requested_by_customer');
    setRefundOpen(false);
  }, [order?.id]);

  if (!order) return null;
  const ship = order.shipping || {};
  const refundedAmount = Number(order.refunded_amount || 0);
  const remaining = Math.max(0, Number(order.amount || 0) - refundedAmount);
  const canRefund = remaining > 0 && order.payment_intent;
  const isShipped = order.fulfilment_status === 'shipped';

  const updateStatus = async (next) => {
    setSavingStatus(next);
    try {
      const payload = { fulfilment_status: next };
      if (next === 'shipped') {
        payload.tracking_number = tracking.trim() || null;
        payload.carrier = carrier.trim() || null;
      }
      const r = await adminOrdersAPI.updateFulfilment(order.id, payload);
      if (next === 'shipped' && !order.tracking_number) {
        if (r.email_live && r.shipped_email_id) {
          toast.success('Marked shipped · customer email sent');
        } else if (!r.email_live) {
          toast.success('Marked shipped · (email service inactive)');
        } else {
          toast.success('Marked shipped');
        }
      } else {
        toast.success(`Order marked ${next}`);
      }
      onChanged?.();
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setSavingStatus('');
    }
  };

  const resendEmails = async () => {
    setResending(true);
    try {
      const r = await adminOrdersAPI.resendEmails(order.id);
      if (r.live) {
        toast.success(`Emails dispatched · customer:${r.customer_email_id || 'n/a'} · vendor:${r.vendor_email_id || 'n/a'}`);
      } else {
        toast.info('Email service not live (RESEND_API_KEY missing). Emails were skipped.');
      }
      onChanged?.();
    } catch (e) {
      toast.error('Failed to resend emails');
    } finally {
      setResending(false);
    }
  };

  const submitRefund = async () => {
    setRefunding(true);
    try {
      const r = await adminOrdersAPI.refund(order.id, {
        amount: refundAmount === '' ? undefined : Number(refundAmount),
        reason: refundReason,
      });
      toast.success(`Refunded $${r.refund.amount.toFixed(2)} · status ${r.status}`);
      setRefundOpen(false);
      setRefundAmount('');
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Order {order.id}</span>
            <Badge className={fulfilmentColor(order.fulfilment_status)}>
              {order.fulfilment_status || 'pending'}
            </Badge>
            <Badge variant="outline">{order.status}</Badge>
          </DialogTitle>
          <DialogDescription>
            Customer, shipping, and line items for this paid Stripe order. Update fulfilment status or resend emails below.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-gray-500">Paid</div>
            <div>{formatDate(order.paid_at || order.created_at)}</div>
            <div className="text-xs text-gray-500 mt-2">Stripe session</div>
            <div className="font-mono text-xs break-all">{order.session_id}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-gray-500">Customer</div>
            <div className="font-medium">{order.customer_name || '—'}</div>
            <div>{order.customer_email || '—'}</div>
          </div>
          <div className="space-y-1 md:col-span-2">
            <div className="text-xs uppercase tracking-wide text-gray-500">Ship to</div>
            <div>
              {ship.name && <div>{ship.name}</div>}
              {ship.line1 && <div>{ship.line1}</div>}
              {ship.line2 && <div>{ship.line2}</div>}
              {(ship.city || ship.state || ship.postal_code) && (
                <div>{ship.city} {ship.state} {ship.postal_code}</div>
              )}
              {ship.country && <div>{ship.country}</div>}
              {!ship.line1 && <div className="text-gray-400">— not provided —</div>}
            </div>
          </div>
        </div>

        <div className="border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left p-2">Product</th>
                <th className="text-center p-2 w-16">Qty</th>
                <th className="text-right p-2 w-28">Unit</th>
                <th className="text-right p-2 w-28">Line</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((it, i) => (
                <tr key={`${it.productId || 'line'}-${i}`} className="border-t">
                  <td className="p-2">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-gray-500">{it.productId}</div>
                  </td>
                  <td className="text-center p-2">{it.qty}</td>
                  <td className="text-right p-2">{money(it.unit_price, order.currency)}</td>
                  <td className="text-right p-2 font-medium">{money(it.line_total, order.currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t bg-gray-50">
                <td colSpan={3} className="p-2 text-right font-semibold">Total</td>
                <td className="p-2 text-right font-bold text-blue-600">{money(order.amount, order.currency)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {order.email_dispatch && (
          <div className="text-xs text-gray-500 border rounded-md p-3 bg-gray-50">
            <div className="font-medium text-gray-700 mb-1">Last email dispatch</div>
            <div>Live: <span className={order.email_dispatch.live ? 'text-emerald-700' : 'text-gray-500'}>{order.email_dispatch.live ? 'yes' : 'no (RESEND_API_KEY missing)'}</span></div>
            <div>Customer email ID: <span className="font-mono">{order.email_dispatch.customer_email_id || 'n/a'}</span></div>
            <div>Vendor email ID: <span className="font-mono">{order.email_dispatch.vendor_email_id || 'n/a'}</span></div>
            <div>Dispatched at: {formatDate(order.email_dispatch.dispatched_at)}</div>
          </div>
        )}

        {/* Tracking number + carrier input — used when marking as shipped */}
        <div className="border rounded-md p-3 bg-blue-50/40 space-y-2">
          <div className="text-xs uppercase tracking-wide text-blue-700 font-medium">Shipping details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Tracking number</label>
              <Input
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="e.g. AP123456789AU"
                data-testid="order-tracking-number"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Carrier</label>
              <Input
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                placeholder="e.g. AusPost, StarTrack, DHL"
                data-testid="order-carrier"
              />
            </div>
          </div>
          {isShipped && (order.tracking_number || order.carrier) && (
            <p className="text-xs text-gray-600">
              Current: <span className="font-mono">{order.tracking_number || '—'}</span>
              {order.carrier ? ` (${order.carrier})` : ''}
            </p>
          )}
          <p className="text-xs text-gray-500">Saved when you click <strong>Mark shipped</strong>. A shipped email is sent to the customer the first time the order transitions to shipped (or skipped if email service is inactive).</p>
        </div>

        {/* Refund summary */}
        {(refundedAmount > 0 || (order.refunds || []).length > 0) && (
          <div className="text-xs border rounded-md p-3 bg-amber-50/40">
            <div className="font-medium text-amber-900 mb-1">Refund history</div>
            <div className="space-y-1">
              {(order.refunds || []).map((r, idx) => (
                <div key={r.id || `refund-${idx}`} className="flex items-center justify-between gap-2">
                  <span className="font-mono text-gray-600">{r.id}</span>
                  <span className="text-gray-600">{formatDate(r.created_at)}</span>
                  <span className="text-gray-600">{r.reason || '—'}</span>
                  <span className="font-semibold">{money(r.amount, r.currency || order.currency)}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between border-t pt-1">
              <span>Total refunded</span>
              <span className="font-semibold">{money(refundedAmount, order.currency)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Remaining</span>
              <span className="font-semibold">{money(remaining, order.currency)}</span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={resendEmails} disabled={resending} data-testid="order-resend-emails">
              <Mail className="h-4 w-4 mr-2" />{resending ? 'Sending…' : 'Resend emails'}
            </Button>
            <Button
              variant="outline"
              className="text-red-600 hover:bg-red-50 border-red-200"
              disabled={!canRefund}
              onClick={() => setRefundOpen(true)}
              data-testid="order-open-refund"
              title={!canRefund ? (order.payment_intent ? 'Fully refunded' : 'No payment_intent on order') : ''}
            >
              <Undo2 className="h-4 w-4 mr-2" /> Refund…
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={order.fulfilment_status === 'pending' ? 'default' : 'outline'}
              onClick={() => updateStatus('pending')}
              disabled={savingStatus === 'pending'}
              data-testid="order-mark-pending"
            >Mark pending</Button>
            <Button
              variant={order.fulfilment_status === 'shipped' ? 'default' : 'outline'}
              className={order.fulfilment_status === 'shipped' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              onClick={() => updateStatus('shipped')}
              disabled={savingStatus === 'shipped'}
              data-testid="order-mark-shipped"
            >Mark shipped</Button>
            <Button
              variant="outline"
              className="text-red-600 hover:bg-red-50"
              onClick={() => updateStatus('cancelled')}
              disabled={savingStatus === 'cancelled'}
              data-testid="order-mark-cancelled"
            >Cancel</Button>
          </div>
        </DialogFooter>

        {/* Refund dialog (nested inside the modal) */}
        <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
          <DialogContent className="max-w-md" data-testid="refund-dialog">
            <DialogHeader>
              <DialogTitle>Refund order {order.id}</DialogTitle>
              <DialogDescription>
                Refund up to {money(remaining, order.currency)} via Stripe. The customer will receive a refund email if the email service is live.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-600">Amount ({(order.currency || 'AUD').toUpperCase()})</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={remaining}
                  placeholder={`Leave blank for full ${money(remaining, order.currency)}`}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="refund-amount-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Reason</label>
                <Select value={refundReason} onValueChange={setRefundReason}>
                  <SelectTrigger data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                    <SelectItem value="duplicate">Duplicate</SelectItem>
                    <SelectItem value="fraudulent">Fraudulent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={refunding}>Cancel</Button>
              <Button
                onClick={submitRefund}
                disabled={refunding}
                className="bg-red-600 hover:bg-red-700"
                data-testid="refund-submit"
              >
                {refunding ? 'Refunding…' : `Refund ${refundAmount ? money(Number(refundAmount), order.currency) : money(remaining, order.currency)}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

const OrdersAdmin = () => {
  const [data, setData] = useState({ orders: [], total: 0, page: 1, pages: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    fulfilment: 'all',
    date_from: '',
    date_to: '',
    page: 1,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: 20,
      };
      if (filters.search) params.search = filters.search;
      if (filters.fulfilment && filters.fulfilment !== 'all') params.fulfilment = filters.fulfilment;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await adminOrdersAPI.list(params);
      setData(res);
    } catch (e) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const p = {};
      if (filters.fulfilment && filters.fulfilment !== 'all') p.fulfilment = filters.fulfilment;
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
      await adminOrdersAPI.downloadCsv(p);
    } catch {
      toast.error('CSV export failed');
    }
  };

  const clearFilters = () => setFilters({ search: '', fulfilment: 'all', date_from: '', date_to: '', page: 1 });

  const runRecoveryScan = async () => {
    try {
      const r = await adminOrdersAPI.runRecoveryScan();
      toast.success(
        `Recovery scan complete · ${r.candidates} candidates · ${r.emails_sent} emails sent · ${r.paid_finalised} late paid orders finalised`
      );
      load();
    } catch {
      toast.error('Recovery scan failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-admin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Orders</h2>
          <p className="text-gray-600 text-sm mt-1">{data.total.toLocaleString()} paid orders · click a row to view details</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={runRecoveryScan} data-testid="orders-recovery-scan" title="Scan for abandoned Stripe sessions and email a recovery link">
            <Inbox className="h-4 w-4 mr-2" /> Recovery scan
          </Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="orders-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={exportCsv} className="bg-blue-600 hover:bg-blue-700" data-testid="orders-export-csv">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs uppercase font-medium text-gray-500 block mb-1">Search</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Order ID, email, or session…"
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
                  className="pl-9"
                  data-testid="orders-search"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-medium text-gray-500 block mb-1">Fulfilment</label>
              <Select value={filters.fulfilment} onValueChange={(v) => setFilters((f) => ({ ...f, fulfilment: v, page: 1 }))}>
                <SelectTrigger data-testid="orders-filter-fulfilment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase font-medium text-gray-500 block mb-1">From</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value, page: 1 }))} data-testid="orders-date-from" />
            </div>
            <div>
              <label className="text-xs uppercase font-medium text-gray-500 block mb-1">To</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value, page: 1 }))} data-testid="orders-date-to" />
            </div>
          </div>
          {(filters.search || filters.fulfilment !== 'all' || filters.date_from || filters.date_to) && (
            <div className="mt-3">
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="orders-clear-filters">
                <X className="h-3 w-3 mr-1" /> Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left p-3">Order</th>
                  <th className="text-left p-3 hidden md:table-cell">Customer</th>
                  <th className="text-left p-3 hidden lg:table-cell">Date</th>
                  <th className="text-center p-3">Items</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-center p-3">Fulfilment</th>
                  <th className="p-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="text-center text-gray-500 py-10">Loading orders…</td></tr>
                )}
                {!loading && data.orders.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-500 py-10">
                    No orders yet. Once customers pay via Stripe, they'll appear here.
                  </td></tr>
                )}
                {!loading && data.orders.map((o) => (
                  <tr
                    key={o.id}
                    className="border-t hover:bg-blue-50/40 cursor-pointer"
                    onClick={() => setSelected(o)}
                    data-testid={`order-row-${o.id}`}
                  >
                    <td className="p-3">
                      <div className="font-mono font-medium">{o.id}</div>
                      <div className="text-xs text-gray-400 hidden lg:block">{o.session_id?.slice(0, 24)}…</div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div className="font-medium truncate max-w-[200px]">{o.customer_name || '—'}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">{o.customer_email || '—'}</div>
                    </td>
                    <td className="p-3 hidden lg:table-cell text-gray-600">{formatDate(o.paid_at || o.created_at)}</td>
                    <td className="p-3 text-center">{(o.items || []).reduce((s, it) => s + (it.qty || 0), 0)}</td>
                    <td className="p-3 text-right font-semibold">{money(o.amount, o.currency)}</td>
                    <td className="p-3 text-center">
                      <Badge className={fulfilmentColor(o.fulfilment_status)}>
                        {o.fulfilment_status || 'pending'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center"><Eye className="h-4 w-4 text-gray-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {data.pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Page {data.page} of {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={data.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              data-testid="orders-prev-page">Previous</Button>
            <Button variant="outline" size="sm" disabled={data.page >= data.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              data-testid="orders-next-page">Next</Button>
          </div>
        </div>
      )}

      <OrderDetail order={selected} onClose={() => setSelected(null)} onChanged={() => { load(); /* keep modal open to show new state */ adminOrdersAPI.get(selected.id).then(setSelected).catch(() => setSelected(null)); }} />
    </div>
  );
};

export default OrdersAdmin;
